import { useMemo, useState } from 'react'
import { Autocomplete, TextField } from '@mui/material'
import GENDERS from '../../constants/genders'
import FilterDropdown from '../FilterDropdown'
import SearchInput from '../SearchInput'
import { Header } from './styles'
import AutocompleteInput from '../UI/AutocompleteInput'
import { isNumber } from '../../utils/helpers'
import { SpecialtyAreaList, SpecialtyListItem } from '../../utils/hooks'

type FiltersHeaderProps = {
  handleFilterChange: (value: { [key: string]: string }) => void
  specialtyAreaOptions: SpecialtyAreaList
  specialtyOptions: Array<SpecialtyListItem>
  languageOptions: Array<string>
  locationOptions: Array<string>
  nameOptions: Array<string>
  titleOptions: Array<string>
}

type specialtyValueType = {
  groupBy: string
  label: string
}

const FiltersHeader = ({
  handleFilterChange,
  specialtyAreaOptions,
  specialtyOptions,
  languageOptions,
  locationOptions,
  nameOptions,
  titleOptions,
}: FiltersHeaderProps): JSX.Element => {
  const [specialtyAreaValue, setSpecialtyAreaValue] = useState<null | string>(
    null
  )
  const [specialtyValue, setSpecialtyValue] =
    useState<null | specialtyValueType>(null)
  const [genderValue, setGenderValue] = useState<null | string>(null)
  const [languageValue, setLanguageValue] = useState<null | string>(null)
  const [locationValue, setLocationValue] = useState<null | string>(null)
  const [ageValue, setAgeValue] = useState<null | number>(null)
  const [titleValue, setTitleValue] = useState<null | string>(null)
  const onlyOneArea = useMemo(
    () =>
      !specialtyOptions.every(
        (opt) => opt.groupBy === specialtyOptions[0].groupBy
      ),
    [specialtyOptions]
  )

  const handleSpecialtyChange = (val: specialtyValueType | null) => {
    const filter: { [key: string]: string } = { specialty: val?.label || '' }
    if (val) {
      filter.specialtyArea = val.groupBy
      setSpecialtyAreaValue(val.groupBy)
    }
    setSpecialtyValue(val)
    handleFilterChange(filter)
  }

  const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      isNumber((e.nativeEvent as InputEvent).data || '0') &&
      Number(e.target.value) >= 0 &&
      Number(e.target.value) <= 160
    ) {
      handleFilterChange({ age: e.target.value })
      setAgeValue(Number(e.target.value))
    }
  }
  return (
    <Header>
      <FilterDropdown
        title="Area"
        className="specialty-area"
        value={specialtyAreaValue || ''}
        options={specialtyAreaOptions.map((spa) => ({
          name: spa.name,
          value: spa.key,
        }))}
        onChange={(val) => {
          setSpecialtyAreaValue(val)
          setSpecialtyValue(null)
          handleFilterChange({ specialtyArea: val, specialty: '' })
        }}
      />
      <Autocomplete
        className="specialty"
        value={specialtyValue}
        isOptionEqualToValue={(opt, val) =>
          opt.label === val.label && opt.groupBy === val.groupBy
        }
        onChange={(_, val) => handleSpecialtyChange(val)}
        groupBy={
          onlyOneArea
            ? (opt) =>
                specialtyAreaOptions.find((area) => area.key === opt.groupBy)
                  ?.name || ''
            : undefined
        }
        options={specialtyOptions}
        renderInput={(params) => (
          <AutocompleteInput {...params} placeholder="Specialty" />
        )}
      />
      <FilterDropdown
        title="Gender"
        className="gender"
        value={genderValue || ''}
        options={GENDERS}
        onChange={(val) => {
          setGenderValue(val)
          handleFilterChange({ gender: val })
        }}
      />
      <FilterDropdown
        title="Language"
        className="language"
        value={languageValue || ''}
        options={languageOptions.map((lang) => ({ name: lang, value: lang }))}
        onChange={(val) => {
          setLanguageValue(val)
          handleFilterChange({ language: val })
        }}
      />
      <FilterDropdown
        title="Location"
        className="location"
        value={locationValue || ''}
        options={locationOptions.map((loc) => ({ name: loc, value: loc }))}
        onChange={(val) => {
          setLocationValue(val)
          handleFilterChange({ location: val })
        }}
      />
      <TextField
        label=""
        placeholder="Patient Age"
        variant="standard"
        className="age"
        value={ageValue || ''}
        onChange={handleAgeChange}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
      />
      <FilterDropdown
        title="Title"
        className="title"
        value={titleValue || ''}
        options={titleOptions.map((title) => ({ name: title, value: title }))}
        onChange={(val) => {
          setTitleValue(val)
          handleFilterChange({ title: val })
        }}
      />
      <SearchInput
        onChange={(val) => handleFilterChange({ search: val })}
        options={nameOptions}
      />
    </Header>
  )
}

export default FiltersHeader
